import {Component, Input, OnInit} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BreadcrumbService} from 'xng-breadcrumb';
import {TokenStorageService} from '../_services/token-storage.service';
import {
  DropshipFulfillmentUpdate,
  DropshipImportListingRequest,
  DropshipMapping,
  DropshipService,
  SupplierCatalog,
  SupplierCategory,
  SupplierConnection,
  SupplierConnectionRequest,
  SupplierProduct,
  SupplierVariant
} from '../shared/dropship.service';
import {OrderView} from '../shared/order.service';

const IMPORT_TAXONOMY_KEY = 'dropship.import.taxonomy';
const IMPORT_MARKUP_KEY = 'dropship.import.markup';
const IMPORT_CONNECTIVITY_KEY = 'dropship.import.connectivity';
const IMPORT_POWER_KEY = 'dropship.import.power';
const IMPORT_CONDITION_KEY = 'dropship.import.condition';

@Component({
  standalone: false,
  selector: 'app-dropship',
  templateUrl: './dropship.component.html',
  styleUrls: ['./dropship.component.css']
})
export class DropshipComponent implements OnInit {
  /** When true, hide standalone page chrome (used inside Profile tabs). */
  @Input() embedded = false;

  tab: 'inbox' | 'catalog' | 'mappings' | 'connections' = 'inbox';
  statusFilter = 'awaiting_seller';
  orders: OrderView[] = [];
  mappings: DropshipMapping[] = [];
  suppliers: SupplierCatalog[] = [];
  connections: SupplierConnection[] = [];
  loading = false;
  error = '';
  success = '';

  drafts: Record<string, DropshipFulfillmentUpdate> = {};

  // Catalog browse
  catalogSupplier = 'eprolo';
  catalogSource: 'import' | 'mine' | 'platform' = 'import';
  catalogCategories: SupplierCategory[] = [];
  catalogSubcategories: SupplierCategory[] = [];
  catalogCategoryId = '';
  catalogSubcategoryId = '';
  catalogProducts: SupplierProduct[] = [];
  catalogPage = 0;
  catalogHasMore = false;
  catalogTotalHint: number | null = null;
  catalogNote = '';
  catalogLoading = false;
  catalogLoadingMore = false;
  catalogCatCounts: Record<string, number> = {};
  catalogSubCounts: Record<string, number> = {};
  selectedProduct: SupplierProduct | null = null;
  selectedProductLoading = false;
  /** Gallery hero image for the open product detail. */
  detailHeroImage = '';
  /** Selected color (option1) when variants are grouped. */
  detailColor = '';
  /** Selected size (option2) when variants are grouped. */
  detailSize = '';
  /** Resolved variant for the current color/size selection (Map target). */
  detailVariant: SupplierVariant | null = null;
  /** Inline success/error under the product detail Map control. */
  detailMapMessage = '';
  detailMapError = '';
  /** Trusted HTML description from supplier. */
  detailDescriptionHtml: SafeHtml | null = null;

  /** Import-as-listing form (catalog detail). */
  importMarkupPercent = 50;
  importTitle = '';
  importPrice: number | null = null;
  importShipsInDays = 7;
  importTaxonomyKey = 'smart-home/lighting/smart-bulbs';
  importConnectivity = 'wifi';
  importPowerSource = 'mains_plug';
  importCondition = 'new';
  importBusy = false;
  importMessage = '';
  importError = '';

  readonly importConnectivityOptions = [
    {key: 'wifi', label: 'Wi‑Fi'},
    {key: 'bluetooth', label: 'Bluetooth'},
    {key: 'zigbee', label: 'Zigbee'},
    {key: 'zwave', label: 'Z‑Wave'},
    {key: 'matter', label: 'Matter'},
    {key: 'ir', label: 'IR'},
    {key: 'rf', label: 'RF'},
    {key: 'wired', label: 'Wired / USB'},
    {key: 'none', label: 'None / standalone'}
  ];

  readonly importPowerSourceOptions = [
    {key: 'mains_plug', label: 'Mains plug'},
    {key: 'hardwired', label: 'Hardwired'},
    {key: 'usb', label: 'USB powered'},
    {key: 'battery', label: 'Battery'},
    {key: 'rechargeable', label: 'Rechargeable'},
    {key: 'solar', label: 'Solar'},
    {key: 'poe', label: 'PoE'}
  ];

  readonly importConditionOptions = [
    {key: 'new', label: 'New'},
    {key: 'like_new', label: 'Like new'},
    {key: 'open_box', label: 'Open box'},
    {key: 'refurbished', label: 'Refurbished'}
  ];

  readonly importTaxonomyOptions: {key: string; label: string; segments: string[]}[] = [
    {key: 'smart-home/lighting/smart-bulbs', label: 'Smart Home · Lighting · Smart bulbs', segments: ['smart-home', 'lighting', 'smart-bulbs']},
    {key: 'smart-home/lighting/led-strips', label: 'Smart Home · Lighting · LED strips', segments: ['smart-home', 'lighting', 'led-strips']},
    {key: 'smart-home/lighting/lamps-night-lights', label: 'Smart Home · Lighting · Lamps & night lights', segments: ['smart-home', 'lighting', 'lamps-night-lights']},
    {key: 'smart-home/plugs-switches/smart-plugs', label: 'Smart Home · Plugs · Smart plugs', segments: ['smart-home', 'plugs-switches', 'smart-plugs']},
    {key: 'smart-home/plugs-switches/smart-switches', label: 'Smart Home · Plugs · Smart switches', segments: ['smart-home', 'plugs-switches', 'smart-switches']},
    {key: 'smart-home/security-cameras/indoor-cameras', label: 'Smart Home · Cameras · Indoor', segments: ['smart-home', 'security-cameras', 'indoor-cameras']},
    {key: 'smart-home/security-cameras/outdoor-cameras', label: 'Smart Home · Cameras · Outdoor', segments: ['smart-home', 'security-cameras', 'outdoor-cameras']},
    {key: 'smart-home/sensors-alarms/motion-door-sensors', label: 'Smart Home · Sensors · Motion / door', segments: ['smart-home', 'sensors-alarms', 'motion-door-sensors']},
    {key: 'smart-home/climate/thermostats-plugs', label: 'Smart Home · Climate · Thermostats', segments: ['smart-home', 'climate', 'thermostats-plugs']},
    {key: 'smart-home/chargers-power/wall-chargers', label: 'Smart Home · Power · Wall chargers', segments: ['smart-home', 'chargers-power', 'wall-chargers']},
    {key: 'smart-home/chargers-power/cables-adapters', label: 'Smart Home · Power · Cables & adapters', segments: ['smart-home', 'chargers-power', 'cables-adapters']},
    {key: 'smart-home/desk-gadgets/usb-hubs-gadgets', label: 'Smart Home · Desk · USB hubs & gadgets', segments: ['smart-home', 'desk-gadgets', 'usb-hubs-gadgets']}
  ];

  connectForm: SupplierConnectionRequest = {
    supplierCode: 'eprolo',
    apiKey: '',
    apiSecret: '',
    storeId: '',
    label: ''
  };

  newMapping: DropshipMapping = {
    title: '',
    supplierSku: '',
    supplierCode: 'manual',
    supplierName: '',
    supplierProductUrl: '',
    supplierVariant: '',
    costCents: undefined,
    currency: 'eur',
    notes: ''
  };

  readonly statusFilters = [
    {value: 'awaiting_seller', label: 'Needs action'},
    {value: 'ordered', label: 'Ordered'},
    {value: 'shipped', label: 'Shipped'},
    {value: 'fulfilled', label: 'Fulfilled'},
    {value: 'all', label: 'All'}
  ];

  readonly eproloCatalogSources = [
    {value: 'import' as const, label: 'Import list'},
    {value: 'mine' as const, label: 'My products'},
    {value: 'platform' as const, label: 'Platform catalog'}
  ];

  readonly singleCatalogSources = [
    {value: 'platform' as const, label: 'Supplier catalog'}
  ];

  get catalogSources(): {value: 'import' | 'mine' | 'platform'; label: string}[] {
    if (this.isEproloCatalog()) {
      return this.eproloCatalogSources;
    }
    if (this.isBigBuyCatalog()) {
      return [{value: 'platform' as const, label: 'BigBuy catalog'}];
    }
    if (this.isCjCatalog()) {
      return [{value: 'platform' as const, label: 'CJ catalog'}];
    }
    return this.singleCatalogSources;
  }

  isBigBuyCatalog(): boolean {
    return String(this.catalogSupplier || '').toLowerCase() === 'bigbuy';
  }

  isCjCatalog(): boolean {
    return String(this.catalogSupplier || '').toLowerCase() === 'cj';
  }

  isEproloCatalog(): boolean {
    return String(this.catalogSupplier || '').toLowerCase() === 'eprolo';
  }

  constructor(
    private dropship: DropshipService,
    private tokens: TokenStorageService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    if (!this.embedded) {
      this.breadcrumbService.set('@Dropship', 'Dropship');
    }
    if (!this.tokens.getToken()) {
      this.router.navigate(['/login'], {
        queryParams: {returnUrl: this.embedded ? '/profile?tab=dropship' : '/dropship'}
      });
      return;
    }
    this.restoreImportPrefs();
    this.reload();
  }

  get connectableSuppliers(): SupplierCatalog[] {
    return (this.suppliers || []).filter(s => s.code !== 'manual');
  }

  get catalogSuppliers(): SupplierCatalog[] {
    const rows = this.suppliers || [];
    const filtered = rows.filter(s => {
      if (!s || s.code === 'manual') {
        return false;
      }
      // Keep known catalog adapters visible — capability/DB flags have been flaky across restarts.
      const code = String(s.code).toLowerCase();
      if (code === 'eprolo' || code === 'bigbuy' || code === 'cj') {
        return true;
      }
      if (s.importCatalog) {
        return true;
      }
      const caps = s.capabilities || [];
      return caps.includes('import_catalog');
    });
    if (filtered.length) {
      return filtered;
    }
    // Last resort: any non-manual supplier from the API.
    return rows.filter(s => s.code && s.code !== 'manual');
  }

  get activeCategoryId(): string {
    return this.catalogCategoryId || '';
  }

  get activeSubcategoryId(): string {
    return this.catalogSubcategoryId || '';
  }

  isCatSelected(id: string | number | undefined): boolean {
    return String(this.catalogCategoryId || '') === String(id ?? '');
  }

  isSubSelected(id: string | number | undefined): boolean {
    return String(this.catalogSubcategoryId || '') === String(id ?? '');
  }

  setTab(tab: 'inbox' | 'catalog' | 'mappings' | 'connections'): void {
    this.tab = tab;
    this.error = '';
    this.success = '';
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.error = '';
    if (this.tab === 'inbox') {
      this.dropship.fulfillmentQueue(this.statusFilter).subscribe({
        next: rows => {
          this.orders = rows || [];
          for (const o of this.orders) {
            if (!this.drafts[o.orderId]) {
              this.drafts[o.orderId] = {
                supplierPoId: o.supplierPoId || '',
                trackingNumber: o.trackingNumber || '',
                trackingCarrier: o.trackingCarrier || '',
                trackingUrl: o.trackingUrl || ''
              };
            }
          }
          this.loading = false;
        },
        error: err => {
          this.error = err?.error?.error || 'Could not load fulfillment queue.';
          this.loading = false;
        }
      });
    } else if (this.tab === 'catalog') {
      this.dropship.listSuppliers().subscribe({
        next: suppliers => {
          this.suppliers = suppliers || [];
          // Ensure catalog adapters appear even if /suppliers omits capability flags.
          this.ensureCatalogSupplierStub('eprolo', 'Eprolo');
          this.ensureCatalogSupplierStub('bigbuy', 'BigBuy');
          this.ensureCatalogSupplierStub('cj', 'CJ Dropshipping');
          if (!this.catalogSuppliers.some(s => s.code === this.catalogSupplier)) {
            this.catalogSupplier = this.catalogSuppliers[0]?.code || 'eprolo';
          }
          this.normalizeCatalogSourceForSupplier();
          this.loading = false;
          this.loadCatalogCategories();
        },
        error: err => {
          this.error = err?.error?.error || 'Could not load suppliers.';
          // Still allow browsing Eprolo if the suppliers list call fails.
          this.suppliers = [{
            supplierId: 'eprolo',
            code: 'eprolo',
            name: 'Eprolo',
            pushOrder: true,
            pullTracking: true,
            importCatalog: true,
            adapterAvailable: true,
            capabilities: ['push_order', 'pull_tracking', 'import_catalog']
          }];
          this.catalogSupplier = 'eprolo';
          this.loading = false;
          this.loadCatalogCategories();
        }
      });
    } else if (this.tab === 'mappings') {
      this.dropship.listSuppliers().subscribe({
        next: suppliers => {
          this.suppliers = suppliers || [];
        }
      });
      this.dropship.listMappings(true).subscribe({
        next: rows => {
          this.mappings = rows || [];
          this.loading = false;
        },
        error: err => {
          this.error = err?.error?.error || 'Could not load product mappings.';
          this.loading = false;
        }
      });
    } else {
      this.dropship.listSuppliers().subscribe({
        next: suppliers => {
          this.suppliers = suppliers || [];
          if (!this.connectForm.supplierCode && this.connectableSuppliers.length) {
            this.connectForm.supplierCode = this.connectableSuppliers[0].code;
          }
        }
      });
      this.dropship.listConnections().subscribe({
        next: rows => {
          this.connections = rows || [];
          this.loading = false;
        },
        error: err => {
          this.error = err?.error?.error || 'Could not load connections.';
          this.loading = false;
        }
      });
    }
  }

  onCatalogSupplierChange(): void {
    this.catalogCategoryId = '';
    this.catalogSubcategoryId = '';
    this.catalogSubcategories = [];
    this.catalogProducts = [];
    this.selectedProduct = null;
    this.catalogPage = 0;
    this.catalogCatCounts = {};
    this.catalogSubCounts = {};
    this.normalizeCatalogSourceForSupplier();
    this.loadCatalogCategories();
  }

  private normalizeCatalogSourceForSupplier(): void {
    if (this.isBigBuyCatalog() || this.isCjCatalog()) {
      this.catalogSource = 'platform';
      return;
    }
    if (!this.catalogSources.some(s => s.value === this.catalogSource)) {
      this.catalogSource = 'import';
    }
  }

  private ensureCatalogSupplierStub(code: string, name: string): void {
    if (this.suppliers.some(s => String(s.code).toLowerCase() === code)) {
      return;
    }
    this.suppliers = [
      ...this.suppliers,
      {
        supplierId: code,
        code,
        name,
        pushOrder: true,
        pullTracking: true,
        importCatalog: true,
        adapterAvailable: true,
        capabilities: ['push_order', 'pull_tracking', 'import_catalog']
      }
    ];
  }

  onCatalogSourceChange(): void {
    this.catalogPage = 0;
    this.selectedProduct = null;
    // Category filters on platform only apply to the current page and often look "empty".
    // Clear them when switching to platform so the full page of ~20 products shows.
    if (this.catalogSource === 'platform') {
      this.catalogCategoryId = '';
      this.catalogSubcategoryId = '';
      this.catalogSubcategories = [];
    }
    this.refreshCategoryOccupancy();
    this.loadCatalogProducts();
  }

  loadCatalogCategories(): void {
    if (!this.catalogSupplier) {
      return;
    }
    this.catalogLoading = true;
    this.error = '';
    this.dropship.catalogCategories(this.catalogSupplier).subscribe({
      next: rows => {
        this.catalogCategories = rows || [];
        this.catalogLoading = false;
        this.refreshCategoryOccupancy();
        this.loadCatalogProducts();
      },
      error: err => {
        this.error = err?.error?.error || 'Could not load categories. Connect the supplier first.';
        this.catalogCategories = [];
        this.catalogLoading = false;
      }
    });
  }

  /** For import/mine: count products per category so empty chips are obvious. */
  refreshCategoryOccupancy(): void {
    this.catalogCatCounts = {};
    this.catalogSubCounts = {};
    if (!this.catalogSupplier || this.catalogSource === 'platform') {
      return;
    }
    this.dropship.catalogProducts(this.catalogSupplier, {
      source: this.catalogSource,
      page: 0,
      size: 100
    }).subscribe({
      next: page => {
        const cat: Record<string, number> = {};
        const sub: Record<string, number> = {};
        for (const p of page?.products || []) {
          for (const id of this.csvIds(p.categoryIds)) {
            cat[id] = (cat[id] || 0) + 1;
          }
          for (const id of this.csvIds(p.subcategoryIds)) {
            sub[id] = (sub[id] || 0) + 1;
          }
        }
        this.catalogCatCounts = cat;
        this.catalogSubCounts = sub;
      }
    });
  }

  csvIds(raw?: string): string[] {
    if (!raw) {
      return [];
    }
    return String(raw).split(',').map(s => s.trim()).filter(Boolean);
  }

  catCount(id: string | number | undefined): number {
    if (id == null || this.catalogSource === 'platform') {
      return -1;
    }
    return this.catalogCatCounts[String(id)] || 0;
  }

  subCount(id: string | number | undefined): number {
    if (id == null || this.catalogSource === 'platform') {
      return -1;
    }
    return this.catalogSubCounts[String(id)] || 0;
  }

  selectCategory(cat: SupplierCategory | null): void {
    this.catalogCategoryId = cat?.id != null ? String(cat.id) : '';
    this.catalogSubcategoryId = '';
    this.catalogSubcategories = [];
    this.selectedProduct = null;
    this.catalogPage = 0;
    if (!this.catalogCategoryId) {
      this.loadCatalogProducts();
      return;
    }
    this.dropship.catalogCategories(this.catalogSupplier, this.catalogCategoryId).subscribe({
      next: rows => {
        this.catalogSubcategories = rows || [];
        this.loadCatalogProducts();
      },
      error: () => {
        this.catalogSubcategories = [];
        this.loadCatalogProducts();
      }
    });
  }

  selectSubcategory(sub: SupplierCategory | null): void {
    this.catalogSubcategoryId = sub?.id != null ? String(sub.id) : '';
    this.selectedProduct = null;
    this.catalogPage = 0;
    this.loadCatalogProducts();
  }

  searchSubcategoryOnPlatform(): void {
    this.catalogSource = 'platform';
    this.catalogPage = 0;
    this.selectedProduct = null;
    this.loadCatalogProducts();
  }

  loadCatalogProducts(mode: 'replace' | 'append' = 'replace'): void {
    if (!this.catalogSupplier) {
      return;
    }
    if (mode === 'replace') {
      this.catalogLoading = true;
      this.catalogPage = 0;
    } else {
      if (!this.catalogHasMore || this.catalogLoading || this.catalogLoadingMore) {
        return;
      }
      this.catalogLoadingMore = true;
      this.catalogPage += 1;
    }
    this.error = '';
    this.catalogNote = '';
    const pageToLoad = this.catalogPage;
    this.dropship.catalogProducts(this.catalogSupplier, {
      categoryId: this.activeCategoryId || undefined,
      subcategoryId: this.activeSubcategoryId || undefined,
      source: this.catalogSource,
      page: pageToLoad,
      size: 20
    }).subscribe({
      next: page => {
        const incoming = page?.products || [];
        if (mode === 'append') {
          const seen = new Set(this.catalogProducts.map(p => p.id));
          const merged = [...this.catalogProducts];
          for (const p of incoming) {
            if (p?.id && !seen.has(p.id)) {
              seen.add(p.id);
              merged.push(p);
            }
          }
          this.catalogProducts = merged;
        } else {
          this.catalogProducts = incoming;
        }
        this.catalogHasMore = !!page?.hasMore;
        this.catalogTotalHint = page?.totalHint ?? null;
        this.catalogNote = page?.note || (
          this.isCjCatalog()
            ? 'CJ returns one page per request — use Load more for the next page.'
            : (this.isBigBuyCatalog()
              ? 'BigBuy returns one page per request — use Load more for the next page.'
              : (this.catalogSource === 'platform'
                ? 'Eprolo platform API returns 20 products per request — use Load more to fetch the next page.'
                : ''))
        );
        this.catalogLoading = false;
        this.catalogLoadingMore = false;
      },
      error: err => {
        this.error = err?.error?.error || 'Could not load products.';
        if (mode === 'append') {
          // Roll back page bump on failure.
          this.catalogPage = Math.max(0, this.catalogPage - 1);
        } else {
          this.catalogProducts = [];
          this.catalogHasMore = false;
          this.catalogTotalHint = null;
          this.catalogNote = '';
        }
        this.catalogLoading = false;
        this.catalogLoadingMore = false;
      }
    });
  }

  variantCount(p: SupplierProduct): number {
    if (p?.variantCount != null && p.variantCount > 0) {
      return p.variantCount;
    }
    return (p?.variants || []).length;
  }

  loadMoreCatalog(): void {
    this.loadCatalogProducts('append');
  }

  openProduct(product: SupplierProduct): void {
    // Show list variants immediately (platform getproduct often returns No data).
    this.selectedProduct = {
      ...product,
      variants: product.variants ? [...product.variants] : []
    };
    this.applyProductDetailState(this.selectedProduct);
    this.error = '';
    this.selectedProductLoading = true;
    this.dropship.catalogProduct(this.catalogSupplier, product.id).subscribe({
      next: detail => {
        if (detail && (detail.variants || []).length) {
          this.selectedProduct = detail;
        } else if (detail) {
          this.selectedProduct = {
            ...detail,
            variants: (this.selectedProduct?.variants || []).length
              ? this.selectedProduct!.variants
              : (detail.variants || [])
          };
        } else if (!(this.selectedProduct?.variants || []).length) {
          this.error = 'No variants returned for this product.';
        }
        if (this.selectedProduct) {
          this.applyProductDetailState(this.selectedProduct);
        }
        this.selectedProductLoading = false;
      },
      error: err => {
        this.selectedProductLoading = false;
        if (!(this.selectedProduct?.variants || []).length) {
          this.error = err?.error?.error || 'Could not load product detail / variants.';
        }
      }
    });
  }

  closeProduct(): void {
    this.selectedProduct = null;
    this.detailHeroImage = '';
    this.detailColor = '';
    this.detailSize = '';
    this.detailVariant = null;
    this.detailMapMessage = '';
    this.detailMapError = '';
    this.detailDescriptionHtml = null;
    this.importMessage = '';
    this.importError = '';
    this.importBusy = false;
  }

  productGallery(p: SupplierProduct | null): string[] {
    if (!p) {
      return [];
    }
    const out: string[] = [];
    const push = (u?: string) => {
      const url = (u || '').trim();
      if (!url) {
        return;
      }
      if (!out.some(x => x.toLowerCase() === url.toLowerCase())) {
        out.push(url);
      }
    };
    for (const u of p.imageUrls || []) {
      push(u);
    }
    push(p.imageUrl);
    for (const v of p.variants || []) {
      push(v.imageUrl);
    }
    return out;
  }

  /** Distinct option1 values (colors) in stable order. */
  productColors(p: SupplierProduct | null): string[] {
    const variants = p?.variants || [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of variants) {
      const c = (v.option1 || '').trim();
      if (!c || seen.has(c)) {
        continue;
      }
      seen.add(c);
      out.push(c);
    }
    return out;
  }

  /** Distinct option2 values (sizes) for the selected color, or all sizes when no colors. */
  productSizes(p: SupplierProduct | null): string[] {
    const variants = p?.variants || [];
    const color = (this.detailColor || '').trim();
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of variants) {
      if (color && (v.option1 || '').trim() !== color) {
        continue;
      }
      const s = (v.option2 || '').trim();
      if (!s || seen.has(s)) {
        continue;
      }
      seen.add(s);
      out.push(s);
    }
    return out;
  }

  /** True when we can show Color → Size chips instead of a flat table. */
  hasGroupedOptions(p: SupplierProduct | null): boolean {
    return this.productColors(p).length > 1 || this.productSizes(p).length > 1;
  }

  selectDetailColor(color: string): void {
    this.detailColor = color;
    const sizes = this.productSizes(this.selectedProduct);
    if (!sizes.includes(this.detailSize)) {
      this.detailSize = sizes[0] || '';
    }
    this.refreshDetailVariant();
  }

  selectDetailSize(size: string): void {
    this.detailSize = size;
    this.refreshDetailVariant();
  }

  selectGalleryImage(url: string): void {
    this.detailHeroImage = url;
  }

  /** Variant matching the current color/size chips (or null). */
  selectedVariant(p: SupplierProduct | null): SupplierVariant | null {
    if (!p?.variants?.length) {
      return null;
    }
    const color = (this.detailColor || '').trim();
    const size = (this.detailSize || '').trim();
    const match = p.variants.find(v =>
      (!color || (v.option1 || '').trim() === color)
      && (!size || (v.option2 || '').trim() === size)
    );
    if (match) {
      return match;
    }
    // If chips couldn't resolve (bad option split), fall back to first variant of the color.
    if (color) {
      return p.variants.find(v => (v.option1 || '').trim() === color) || null;
    }
    return p.variants[0] || null;
  }

  colorSwatch(p: SupplierProduct, color: string): string | null {
    const match = (p.variants || []).find(v => (v.option1 || '').trim() === color && v.imageUrl);
    return match?.imageUrl || null;
  }

  private applyProductDetailState(p: SupplierProduct): void {
    const gallery = this.productGallery(p);
    this.detailHeroImage = gallery[0] || p.imageUrl || '';
    const colors = this.productColors(p);
    this.detailColor = colors[0] || '';
    const sizes = this.productSizes(p);
    this.detailSize = sizes[0] || '';
    this.detailMapMessage = '';
    this.detailMapError = '';
    this.importMessage = '';
    this.importError = '';
    this.importTitle = (p.title || '').trim();
    this.refreshDetailVariant();
    const raw = (p.description || '').trim();
    this.detailDescriptionHtml = raw
      ? this.sanitizer.bypassSecurityTrustHtml(raw)
      : null;
  }

  private refreshDetailVariant(): void {
    this.detailVariant = this.selectedVariant(this.selectedProduct);
    if (this.detailVariant?.imageUrl) {
      this.detailHeroImage = this.detailVariant.imageUrl;
    }
    this.recalcImportPriceFromCost();
  }

  onImportMarkupChange(): void {
    this.persistImportPrefs();
    this.recalcImportPriceFromCost();
  }

  onImportTaxonomyChange(): void {
    this.persistImportPrefs();
  }

  onImportAttrChange(): void {
    this.persistImportPrefs();
  }

  private recalcImportPriceFromCost(): void {
    const cost = this.detailVariant?.costUsd;
    if (cost == null || !Number.isFinite(cost) || cost <= 0) {
      return;
    }
    const markup = Number(this.importMarkupPercent);
    const factor = 1 + (Number.isFinite(markup) ? markup : 0) / 100;
    this.importPrice = Math.round(cost * factor * 100) / 100;
  }

  private restoreImportPrefs(): void {
    try {
      const taxonomy = localStorage.getItem(IMPORT_TAXONOMY_KEY);
      if (taxonomy && this.importTaxonomyOptions.some(o => o.key === taxonomy)) {
        this.importTaxonomyKey = taxonomy;
      }
      const markupRaw = localStorage.getItem(IMPORT_MARKUP_KEY);
      if (markupRaw != null && markupRaw !== '') {
        const markup = Number(markupRaw);
        if (Number.isFinite(markup) && markup >= 0) {
          this.importMarkupPercent = markup;
        }
      }
      const connectivity = localStorage.getItem(IMPORT_CONNECTIVITY_KEY);
      if (connectivity && this.importConnectivityOptions.some(o => o.key === connectivity)) {
        this.importConnectivity = connectivity;
      }
      const power = localStorage.getItem(IMPORT_POWER_KEY);
      if (power && this.importPowerSourceOptions.some(o => o.key === power)) {
        this.importPowerSource = power;
      }
      const condition = localStorage.getItem(IMPORT_CONDITION_KEY);
      if (condition && this.importConditionOptions.some(o => o.key === condition)) {
        this.importCondition = condition;
      }
    } catch {
      // ignore storage errors
    }
  }

  private persistImportPrefs(): void {
    try {
      localStorage.setItem(IMPORT_TAXONOMY_KEY, this.importTaxonomyKey);
      localStorage.setItem(IMPORT_MARKUP_KEY, String(this.importMarkupPercent));
      localStorage.setItem(IMPORT_CONNECTIVITY_KEY, this.importConnectivity);
      localStorage.setItem(IMPORT_POWER_KEY, this.importPowerSource);
      localStorage.setItem(IMPORT_CONDITION_KEY, this.importCondition);
    } catch {
      // ignore storage errors
    }
  }

  private resolveSupplierSku(variant: SupplierVariant | null): string | undefined {
    if (!variant) {
      return undefined;
    }
    if (this.isBigBuyCatalog()) {
      return variant.sku || variant.id;
    }
    return variant.id || variant.sku;
  }

  importSelectedAsListing(product: SupplierProduct, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const variant = this.detailVariant || this.selectedVariant(product);
    if (!variant) {
      this.importError = 'Select a color/size first.';
      this.importMessage = '';
      return;
    }
    this.importVariantAsListing(product, variant);
  }

  importVariantAsListing(product: SupplierProduct, variant: SupplierVariant, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.error = '';
    this.success = '';
    this.importMessage = '';
    this.importError = '';
    this.detailVariant = variant;
    if (variant.imageUrl) {
      this.detailHeroImage = variant.imageUrl;
    }
    this.recalcImportPriceFromCost();

    const supplierSku = this.resolveSupplierSku(variant);
    if (!supplierSku) {
      const msg = this.isBigBuyCatalog()
        ? 'Variant has no SKU (BigBuy reference).'
        : (this.isCjCatalog()
          ? 'Variant has no vid (CJ variant id).'
          : 'Variant has no id (variantsid).');
      this.importError = msg;
      return;
    }

    const title = (this.importTitle || product.title || '').trim();
    if (!title) {
      this.importError = 'Title is required.';
      return;
    }
    const price = Number(this.importPrice);
    if (!Number.isFinite(price) || price <= 0) {
      this.importError = 'Sell price must be greater than 0.';
      return;
    }
    const taxonomy = this.importTaxonomyOptions.find(o => o.key === this.importTaxonomyKey)
      || this.importTaxonomyOptions[0];
    if (!taxonomy) {
      this.importError = 'Pick a site category.';
      return;
    }

    const imageUrls = this.productGallery(product).slice(0, 3);
    if (variant.imageUrl) {
      const hero = variant.imageUrl.trim();
      if (hero && !imageUrls.some(u => u.toLowerCase() === hero.toLowerCase())) {
        imageUrls.unshift(hero);
      }
    }

    const costCents = variant.costUsd != null ? Math.round(variant.costUsd * 100) : undefined;
    const body: DropshipImportListingRequest = {
      taxonomySegments: taxonomy.segments,
      title,
      description: product.description || title,
      price,
      shipsInDays: this.importShipsInDays > 0 ? this.importShipsInDays : 7,
      supplierCode: this.catalogSupplier,
      supplierSku,
      supplierName: this.catalogSupplier,
      supplierVariant: variant.title || [variant.option1, variant.option2, variant.option3].filter(Boolean).join(' / '),
      supplierProductId: product.id,
      costCents,
      currency: this.isBigBuyCatalog() ? 'eur' : 'usd',
      imageUrls: imageUrls.slice(0, 3),
      createMapping: true,
      attributes: {
        connectivity: this.importConnectivity,
        power_source: this.importPowerSource,
        condition_grade: this.importCondition
      }
    };

    this.importBusy = true;
    this.persistImportPrefs();
    this.dropship.importListing(body).subscribe({
      next: result => {
        this.importBusy = false;
        const msg = `Imported listing ${result.itemId}`
          + (result.mappingId ? ` · mapping ${result.mappingId}` : '');
        this.importMessage = msg;
        this.success = msg;
        this.importError = '';
      },
      error: err => {
        this.importBusy = false;
        const msg = err?.error?.error || 'Could not import listing.';
        this.importError = msg;
        this.error = msg;
        this.importMessage = '';
      }
    });
  }

  /** Map the chip-selected variant (avoids fragile *ngIf `as v` click binding). */
  mapSelectedVariant(product: SupplierProduct, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const variant = this.detailVariant || this.selectedVariant(product);
    if (!variant) {
      this.detailMapError = 'Select a color/size first.';
      this.detailMapMessage = '';
      return;
    }
    this.mapVariant(product, variant);
  }

  mapVariant(product: SupplierProduct, variant: SupplierVariant, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.error = '';
    this.success = '';
    this.detailMapMessage = '';
    this.detailMapError = '';
    if (!variant) {
      this.detailMapError = 'No variant selected.';
      return;
    }
    // Eprolo: variantsid (variant.id). BigBuy: SKU/reference. CJ: vid (variant.id).
    const supplierSku = this.resolveSupplierSku(variant);
    if (!supplierSku) {
      const msg = this.isBigBuyCatalog()
        ? 'Variant has no SKU (BigBuy reference).'
        : (this.isCjCatalog()
          ? 'Variant has no vid (CJ variant id).'
          : 'Variant has no id (variantsid).');
      this.error = msg;
      this.detailMapError = msg;
      return;
    }
    const costCents = variant.costUsd != null ? Math.round(variant.costUsd * 100) : undefined;
    const body: DropshipMapping = {
      title: product.title,
      supplierSku,
      supplierCode: this.catalogSupplier,
      supplierName: this.catalogSupplier,
      supplierVariant: variant.title || [variant.option1, variant.option2, variant.option3].filter(Boolean).join(' / '),
      costCents,
      currency: this.isBigBuyCatalog() ? 'eur' : 'usd',
      notes: `${this.catalogSupplier} product ${product.id}`
    };
    this.dropship.createMapping(body).subscribe({
      next: () => {
        const msg = `Mapped “${variant.title || supplierSku}” → supplier SKU ${supplierSku}`;
        this.success = msg;
        this.detailMapMessage = msg;
        this.detailMapError = '';
      },
      error: err => {
        const msg = err?.error?.error || 'Could not save mapping.';
        this.error = msg;
        this.detailMapError = msg;
        this.detailMapMessage = '';
      }
    });
  }

  amount(order: OrderView): string {
    return `${(order.amountCents / 100).toFixed(2)} ${(order.currency || '').toUpperCase()}`;
  }

  shipTo(order: OrderView): string {
    return [
      order.shipFullName,
      order.shipLine1,
      order.shipLine2,
      [order.shipCity, order.shipPostalCode].filter(Boolean).join(' '),
      order.shipCountry,
      order.shipPhone
    ].filter(Boolean).join(', ');
  }

  copyShipTo(order: OrderView): void {
    const text = this.shipTo(order);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      this.success = 'Ship-to address copied.';
    }
  }

  markOrdered(order: OrderView): void {
    const draft = this.drafts[order.orderId] || {};
    this.saveFulfillment(order.orderId, {...draft, fulfillmentStatus: 'ordered'});
  }

  markShipped(order: OrderView): void {
    const draft = this.drafts[order.orderId] || {};
    if (!draft.trackingNumber?.trim()) {
      this.error = 'Add a tracking number before marking shipped.';
      return;
    }
    this.saveFulfillment(order.orderId, {...draft, fulfillmentStatus: 'shipped'});
  }

  markFulfilled(order: OrderView): void {
    const draft = this.drafts[order.orderId] || {};
    this.saveFulfillment(order.orderId, {...draft, fulfillmentStatus: 'fulfilled'});
  }

  saveDraft(order: OrderView): void {
    this.saveFulfillment(order.orderId, this.drafts[order.orderId] || {});
  }

  pushToSupplier(order: OrderView): void {
    this.error = '';
    this.success = '';
    this.dropship.pushToSupplier(order.orderId).subscribe({
      next: updated => {
        this.success = updated.supplierPoId
          ? `Pushed to ${updated.supplierCode || 'supplier'} — PO ${updated.supplierPoId}`
          : `Push attempted (${updated.fulfillmentStatus}). Check connection / API config if still Needs action.`;
        delete this.drafts[order.orderId];
        const next = (updated.fulfillmentStatus || '').trim();
        if (next && this.statusFilters.some(f => f.value === next)) {
          this.statusFilter = next;
        }
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Push to supplier failed.';
      }
    });
  }

  syncTracking(order: OrderView): void {
    this.error = '';
    this.success = '';
    this.dropship.syncTracking(order.orderId).subscribe({
      next: updated => {
        this.success = updated.trackingNumber
          ? `Tracking synced: ${updated.trackingNumber}`
          : 'Tracking sync completed.';
        delete this.drafts[order.orderId];
        if (updated.fulfillmentStatus) {
          this.statusFilter = updated.fulfillmentStatus;
        }
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Sync tracking failed.';
      }
    });
  }

  saveConnection(): void {
    this.error = '';
    this.success = '';
    if (!this.connectForm.supplierCode) {
      this.error = 'Pick a supplier.';
      return;
    }
    const code = this.connectForm.supplierCode;
    const existing = this.connections.find(c => c.supplierCode === code);
    const keyFilled = !!this.connectForm.apiKey?.trim();
    const secretFilled = !!this.connectForm.apiSecret?.trim();
    if (code === 'eprolo') {
      const hasKey = keyFilled || !!existing?.hasApiKey;
      const hasSecret = secretFilled || !!existing?.hasApiSecret;
      if (!hasKey || !hasSecret) {
        this.error = 'Eprolo needs both apiKey and apiSecret (from their email / dashboard). Paste both, then Save.';
        return;
      }
    }
    const body: SupplierConnectionRequest = {
      supplierCode: code,
      label: this.connectForm.label,
      storeId: this.connectForm.storeId,
      apiKey: keyFilled ? this.connectForm.apiKey : undefined,
      apiSecret: secretFilled ? this.connectForm.apiSecret : undefined,
      keepExistingKey: !!(existing?.hasApiKey && !keyFilled)
    };
    this.dropship.upsertConnection(body).subscribe({
      next: conn => {
        this.success = conn.status === 'connected'
          ? `${conn.supplierName || conn.supplierCode} connected.`
          : (conn.lastError || 'Saved with errors — check credentials.');
        this.connectForm.apiKey = '';
        this.connectForm.apiSecret = '';
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Could not save connection.';
      }
    });
  }

  testConnection(code: string): void {
    this.dropship.testConnection(code).subscribe({
      next: conn => {
        this.success = conn.lastError || `${conn.supplierCode}: ${conn.status}`;
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Test failed.';
      }
    });
  }

  disconnect(code: string): void {
    this.dropship.disconnect(code).subscribe({
      next: () => {
        this.success = 'Disconnected.';
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Could not disconnect.';
      }
    });
  }

  createMapping(): void {
    this.error = '';
    this.success = '';
    if (!this.newMapping.title?.trim() || !this.newMapping.supplierSku?.trim()) {
      this.error = 'Title and supplier SKU are required.';
      return;
    }
    this.dropship.createMapping(this.newMapping).subscribe({
      next: () => {
        this.success = 'Product mapping saved.';
        this.newMapping = {
          title: '',
          supplierSku: '',
          supplierCode: 'manual',
          supplierName: '',
          supplierProductUrl: '',
          supplierVariant: '',
          costCents: undefined,
          currency: 'eur',
          notes: ''
        };
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Could not save mapping.';
      }
    });
  }

  removeMapping(mapping: DropshipMapping): void {
    if (!mapping.mappingId) {
      return;
    }
    this.dropship.deleteMapping(mapping.mappingId).subscribe({
      next: () => {
        this.success = 'Mapping deactivated.';
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Could not remove mapping.';
      }
    });
  }

  private saveFulfillment(orderId: string, body: DropshipFulfillmentUpdate): void {
    this.error = '';
    this.success = '';
    this.dropship.updateFulfillment(orderId, body).subscribe({
      next: updated => {
        this.success = `Order updated (${updated.fulfillmentStatus}).`;
        delete this.drafts[orderId];
        const next = (updated.fulfillmentStatus || '').trim();
        if (next && this.statusFilters.some(f => f.value === next)) {
          this.statusFilter = next;
        } else if (next) {
          this.statusFilter = 'all';
        }
        this.reload();
      },
      error: err => {
        this.error = err?.error?.error || 'Could not update fulfillment.';
      }
    });
  }
}
